import * as React from "react"
import { useEffect, useState } from "react"
import styled from "styled-components"
import PriceFilter from "./filters/PriceFilter"
import ProductFilterType from "../../model/ProductFilterType"
import BrandFilter from "./filters/BrandFilter"
import ImpactFilter from "./filters/ImpactFilter"
import Accordion from "../accordion/Accordion"
import useQueryString, { useSerializeQueryString } from "../../hooks/useQueryString"
import * as colors from "../../constants/colors"
import { otherBorderGrey } from "../../constants/colors"
import ProductList from "./ProductList"
import RecommendedSorter from "./sorters/RecommendedSorter"
import Sorter from "../../model/Sorter"
import LatestSorter from "./sorters/LatestSorter"
import PopularitySorter from "./sorters/PopularitySorter"
import PriceLowHighSorter from "./sorters/PriceLowHighSorter"
import PriceHighLowSorter from "./sorters/PriceHighLowSorter"
import { Button, Icon } from "semantic-ui-react"
import { SideModal } from "components"
import { ImpactType, ProductType, screenSizes } from "model"

const Container = styled.div`
  display: flex;
  position: relative;
`

const DesktopFilterContainer = styled.div`
  padding-right: 30px;
  width: 250px;

  @media (max-width: ${screenSizes.desktop}px) {
    display: none;
  }
`
const MobileFilterContainer = styled.div`
  display: none;
  @media (max-width: ${screenSizes.desktop}px) {
    display: block;
  }
`
const MobileFilterHeader = styled.div`
  padding: 20px 10px;
  border-bottom: 1px solid ${colors.otherBorderGrey};
`
const MobileFilterInner = styled.div`
  justify-content: space-between;
  display: flex;
  margin-bottom: 20px;

  > div {
    font-weight: bold;
  }
`
const SortContainer = styled.div`
  width: 100%;
  flex: 1;

  select {
    padding: 0;
    border: none;
    text-align: right;
    text-align-last: right;
    padding-right: 5px;
    background-color: unset;
  }
`
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  height: 60px;
  align-items: center;

  @media (max-width: ${screenSizes.largeDesktop}px) {
    padding: 0 10px;
  }

  > div {
    flex: 1;
  }
`

const Filters = styled.div``
const SidebarContent = styled.div<{ showFilterSidebar: boolean }>`
  border-bottom: ${({ showFilterSidebar }) =>
    showFilterSidebar ? `1px solid ${colors.otherBorderGrey}` : "none"};
  padding-bottom: 20px;
`

const NResults = styled.div`
  @media (max-width: ${screenSizes.desktop}px) {
    text-align: center;
  }
`

const SortSelectContainer = styled.div`
  text-align: right;
`

const FilterButton = styled.div`
  display: none;
  cursor: pointer;

  @media (max-width: ${screenSizes.desktop}px) {
    display: block;
  }
`

const filters: ProductFilterType[] = [PriceFilter, BrandFilter, ImpactFilter]

const sorters: Sorter[] = [
  RecommendedSorter,
  LatestSorter,
  PopularitySorter,
  PriceLowHighSorter,
  PriceHighLowSorter,
]

interface PropsType {
  products: ProductType[]
  collectionName: string
  impacts: ImpactType[]
  initialProducts?: number
  sidebarContent?: React.ReactNode
  showFilterSidebar?: boolean
  bottomContent?: React.ReactNode
  hideBrand?: boolean
}

const ProductFilterList = ({
  products,
  collectionName,
  impacts,
  initialProducts = 24,
  sidebarContent,
  showFilterSidebar,
  bottomContent,
  hideBrand,
}: PropsType) => {
  const [state, setState] = useSerializeQueryString("filters", {})
  const [sorterId, setSorterId] = useQueryString("sort", RecommendedSorter.id)
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    const init = async () => {}
    init()
  }, [])

  const handleSetState = (name, value) => setState({ ...state, [name]: value })

  const filteredProducts = products.filter(product =>
    filters.every(filter => filter.filter(product, state[filter.filterName]))
  )

  const sorter = sorters.find(sorter => sorter.id === sorterId)
  const sortedProducts = sorter ? sorter.sort(filteredProducts) : filteredProducts

  const renderFilters = () => (
    <Filters>
      {sidebarContent && (
        <SidebarContent showFilterSidebar={showFilterSidebar}>{sidebarContent}</SidebarContent>
      )}
      {showFilterSidebar && (
        <Accordion>
          {filters.map(Filter => (
            <Filter
              key={Filter.filterName}
              allProducts={products}
              state={state[Filter.filterName]}
              onChange={value => handleSetState(Filter.filterName, value)}
              impacts={impacts}
            />
          ))}
        </Accordion>
      )}
    </Filters>
  )

  return (
    <Container>
      <MobileFilterContainer>
        <SideModal show={showFilters} onClose={() => setShowFilters(false)}>
          <MobileFilterHeader>
            <MobileFilterInner>
              <div>Filters</div>
              <Button onClick={() => setShowFilters(!showFilters)}>Done</Button>
            </MobileFilterInner>
            <div>{filteredProducts.length} Results</div>
          </MobileFilterHeader>
          {renderFilters()}
        </SideModal>
      </MobileFilterContainer>

      {(showFilterSidebar || sidebarContent) && (
        <DesktopFilterContainer>{renderFilters()}</DesktopFilterContainer>
      )}

      <SortContainer>
        <Toolbar>
          <FilterButton onClick={() => setShowFilters(!showFilters)}>
            <Icon name="sliders horizontal" size="small" /> Filter
          </FilterButton>
          <NResults>{filteredProducts.length} Results</NResults>
          <SortSelectContainer>
            <select
              onChange={event => setSorterId(event.target.value)}
              placeholder="Sort By:"
              value={sorterId}
            >
              {sorters.map(sorter => (
                <option value={sorter.id} key={sorter.id}>
                  {sorter.label}
                </option>
              ))}
            </select>
          </SortSelectContainer>
        </Toolbar>
        <ProductList
          products={sortedProducts}
          collectionName={collectionName}
          nProducts={initialProducts}
          hideBrand={hideBrand}
        />
        {bottomContent}
      </SortContainer>
    </Container>
  )
}

export default ProductFilterList
